import { useQueryGetAllMessages } from "@apis:message/message";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useBotSelection } from "@hooks:useBotSelection";
import { useNewChat } from "@hooks:useNewChat";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Tab } from "@pages:app/talk/const";
import { HistoryItem } from "@pages:app/talk/Sidebar/HistoryItem";
import { Toggle } from "@pages:app/talk/Sidebar/Toggle";
import { keepPreviousData } from "@tanstack/react-query";
import { groupObjectByDate } from "@utils:datetime";
import _, { orderBy, uniqBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { AiOutlineClockCircle } from "react-icons/ai";
import { BiMessageRoundedAdd } from "react-icons/bi";
import { BsLayoutSidebar } from "react-icons/bs";

import { FeatureFlag } from "@:flag";

interface SidebarProps {
  tab: Tab;
  setTab: (tab: Tab) => void;
  onToggleSidebar?: () => void;
}

export const Sidebar = ({ tab, setTab, onToggleSidebar }: SidebarProps) => {
  const { newChat } = useNewChat();

  const { getSelectedBot } = useBotSelection();

  // Add keyboard shortcut listener for ⌘+K (Mac) and Ctrl+K (Windows)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for ⌘+K on Mac or Ctrl+K on Windows/Linux
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        newChat();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [newChat]);

  // TODO: We currently support Chat
  const messages = useQueryGetAllMessages({
    select: (data) => {
      return _(data)
        .filter((message) => message.botId === getSelectedBot()?.id)
        .groupBy("conversationId")
        .map((messages) => _.orderBy(messages, ["createdAt"], ["asc"])[0])
        .value();
    },
    placeholderData: keepPreviousData,
  });

  const groupedMessages = useMemo(() => {
    return groupObjectByDate(messages.data || [], "createdAt");
  }, [messages.data]);

  const [hoveredConversationId, setHoveredConversationId] = useState<
    string | undefined
  >();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        width: "275px",
        transform: "translateX(0)",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <Box
        sx={{
          padding: "12px",
          paddingTop: "2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SVGInternalIcon
          icon="nemoIcon"
          sx={{
            width: "120px",
          }}
        />
        {onToggleSidebar && (
          <Tooltip title="Hide Sidebar" placement="right">
            <IconButton onClick={onToggleSidebar}>
              <BsLayoutSidebar size={20} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          mx: "auto",
        }}
      >
        {FeatureFlag.ToggleChatSearch && <Toggle tab={tab} setTab={setTab} />}
      </Box>

      <Box
        sx={{
          marginTop: "15px",
          mx: "auto",
          display: tab === Tab.Chat ? "block" : "none",
        }}
      >
        <Button
          variant="contained"
          startIcon={
            <Stack direction="row" alignItems="center" gap="4px">
              <BiMessageRoundedAdd size={20} />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "15px",
                  lineHeight: "20px",
                }}
              >
                New Chat
              </Typography>
            </Stack>
          }
          endIcon={
            <Stack direction="row" gap="3px">
              <Typography
                sx={{
                  padding: "4px 4px",
                  backgroundColor: "#E0E0E0",
                  borderRadius: "4px",
                  lineHeight: "16px",
                  fontWeight: 600,
                  width: "22px",
                }}
              >
                ⌘
              </Typography>
              <Typography
                sx={{
                  padding: "4px 4px",
                  backgroundColor: "#E0E0E0",
                  borderRadius: "4px",
                  lineHeight: "16px",
                  fontWeight: 600,
                  width: "22px",
                }}
              >
                K
              </Typography>
            </Stack>
          }
          sx={{
            height: "42px",
            backgroundColor: "#FFFFFF",
            color: "#1D1D1D",
            "&:hover": {
              backgroundColor: "#f9f9f9",
              boxShadow: "0px 8px 24px 0px #686B6E29",
            },
            padding: "21px 25px",
            borderRadius: "40px",
            gap: "1.5rem",
          }}
          onClick={() => {
            newChat();
          }}
        />
      </Box>

      {messages.isLoading ? (
        <Box
          sx={{
            display: "flex",
            height: "100%",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress
            sx={{
              color: "#1e9e9e",
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            marginTop: "1rem",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "0.5rem",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "0.25rem",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(0,0,0,0.3)",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          {groupedMessages.map(({ group, items }) => {
            let orderedItems = orderBy(items, ["createdAt"], ["desc"]);

            if (tab === Tab.Search) {
              orderedItems = uniqBy(orderedItems, "question");
            }

            return (
              <Box
                key={group}
                sx={{
                  marginTop: "1rem",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap="4px"
                  sx={{
                    marginTop: "20px",
                    marginBottom: "5px",
                    paddingLeft: "14px",
                  }}
                >
                  <AiOutlineClockCircle color="#a4a4aa" />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      lineHeight: "18px",
                      fontWeight: 500,
                      color: "#a4a4aa",
                    }}
                  >
                    {group}
                  </Typography>
                </Stack>
                {orderedItems.map((message, i) => (
                  <HistoryItem
                    key={`${group}-${message.id}-${i}`}
                    message={message}
                    hoveredConversationId={hoveredConversationId}
                    setHoveredConversationId={setHoveredConversationId}
                    tab={tab}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
